import React, {ReactNode} from 'react'

import {Card} from '@daedalus/atlas/Card'
import {useDeviceLayout} from '@daedalus/atlas/context/deviceLayout'
import {Icon} from '@daedalus/atlas/Icon'
import {Text} from '@daedalus/atlas/Text'
import {cssTheme} from '@daedalus/atlas/themes'
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'

import {CardHeaderLayout, ThickBorderCard} from './RoomVariant'
import {translationMessages} from './translationMessages'
import {RoomCardVariants} from './types'

interface RoomCardVariantPropsTypes {
  variant?: RoomCardVariants
  children: ReactNode
}

const SelectedDealCard = ({children}: {children: ReactNode}) => {
  const {isMobile} = useDeviceLayout()

  return (
    <ThickBorderCard
      borderRadius={isMobile ? 'lg' : 'xl'}
      borderColorPath="border.brand.c300"
      dataId="selectedDealRoomCard"
    >
      <CardHeaderLayout
        bgColor={cssTheme.colors.background.brand.c100}
        contentColor={cssTheme.colors.content.brand.c800}
        iconSlot={<Icon name="ArrowDown" />}
        titleSlot={
          <Text variant="labelM">
            <FormattedMessageWrapper
              id="roomPromotion.yourChoiceBanner"
              defaultMessage="Your selected deal"
            />
          </Text>
        }
      />
      {children}
    </ThickBorderCard>
  )
}

const BestDealCard = ({children}: {children: ReactNode}) => {
  const {isMobile} = useDeviceLayout()
  return (
    <ThickBorderCard
      borderRadius={isMobile ? 'lg' : 'xl'}
      borderColorPath="border.success.c300"
      dataId="bestDealRoomCard"
    >
      <CardHeaderLayout
        bgColor={cssTheme.colors.background.featured.c100}
        contentColor={cssTheme.colors.content.success.c900}
        iconSlot={<Icon name="Party" />}
        titleSlot={
          <Text variant="labelM">
            <FormattedMessageWrapper
              {...translationMessages.bestAvailableDeal}
            />
          </Text>
        }
      />
      {children}
    </ThickBorderCard>
  )
}

const YourFrozenDealCard = ({children}: {children: ReactNode}) => {
  const {isMobile} = useDeviceLayout()
  return (
    <ThickBorderCard
      borderRadius={isMobile ? 'lg' : 'xl'}
      borderColorPath="border.special.c600"
      dataId="selectedDealRoomCard"
    >
      <CardHeaderLayout
        bgColor={cssTheme.colors.background.brand.c100}
        contentColor={cssTheme.colors.content.brand.c800}
        iconSlot={<Icon name="ArrowDown" />}
        titleSlot={
          <Text variant="labelM">
            <FormattedMessageWrapper
              id="df.yourFrozenDeal"
              defaultMessage="Your frozen deal"
            />
          </Text>
        }
      />
      {children}
    </ThickBorderCard>
  )
}

const SplitBookingCheapestCard = ({children}: {children: ReactNode}) => {
  const {isMobile} = useDeviceLayout()
  return (
    <Card
      borderRadius={isMobile ? 'lg' : 'xl'}
      borderColorPath="border.success.c300"
      dataId="splitBookingCheapestRoomCard"
    >
      {children}
    </Card>
  )
}

const DefaultCard = ({children}: {children: ReactNode}) => {
  const {isMobile} = useDeviceLayout()
  return (
    <Card
      borderRadius={isMobile ? 'md' : 'xl'}
      borderColorPath={isMobile ? 'border.neutral.c100' : 'border.neutral.c200'}
      dataId="defaultRoomCard"
    >
      {children}
    </Card>
  )
}

export const RoomCardVariant = ({
  variant = RoomCardVariants.Default,
  children
}: RoomCardVariantPropsTypes) => {
  if (variant === RoomCardVariants.SplitBookingCheapest) {
    return <SplitBookingCheapestCard>{children}</SplitBookingCheapestCard>
  }

  if (variant === RoomCardVariants.BestDeal) {
    return <BestDealCard>{children}</BestDealCard>
  }

  if (variant === RoomCardVariants.SelectedDeal) {
    return <SelectedDealCard>{children}</SelectedDealCard>
  }

  if (variant === RoomCardVariants.YourFrozenDeal) {
    return <YourFrozenDealCard>{children}</YourFrozenDealCard>
  }

  return <DefaultCard>{children}</DefaultCard>
}
